import { useGraphQLQuery } from '@betterment/js-runtime';
import { Button, Card, Dialog, Heading, Text } from '@betterment/samba-react';
import { CardLoader, SambaAutoLink } from '@betterment/shared-components';

import {
  DirectTradingAccountBuySellAvailabilityDocument,
  DirectTradingAccountBuySellAvailabilityQuery,
} from './graphql/directTradingAccountBuySellAvailability.generated';

import { urlBuilder } from '../../../Shared/urlBuilder';
import { Props } from '../../AccountSecurityPage';

type ActionBlockedDialogProps = { direction: 'Buy' | 'Sell'; message: string };

const ActionBlockedDialog: React.FC<ActionBlockedDialogProps> = ({ direction, message }) => {
  const buttonVariant = direction === 'Buy' ? 'solidPrimary' : 'solidSecondary';

  return (
    <Dialog>
      <Dialog.Trigger asChild={true}>
        <Button variant={buttonVariant} spacing="compact" css={{ flex: 1 }}>
          {direction}
        </Button>
      </Dialog.Trigger>
      <Dialog.Content css={{ p: 'space.400', width: '50%' }}>
        <Dialog.Title>
          <Heading>{direction} currently unavailable</Heading>
        </Dialog.Title>
        <Dialog.Description>
          <Text dangerouslySetInnerHTML={{ __html: message }} />
        </Dialog.Description>
        <Dialog.Close asChild={true}>
          <Button variant="solidSecondary" css={{ mt: 'space.300' }}>
            Close
          </Button>
        </Dialog.Close>
      </Dialog.Content>
    </Dialog>
  );
};

type AccountData = DirectTradingAccountBuySellAvailabilityQuery['account'];

type SellButtonProps = {
  securityPosition: AccountData['securityPosition'];
  sellOrderBlockedAvailabilityMessage: AccountData['sellOrderBlockedAvailabilityMessage'];
  accountId: string;
  securityId: string;
};

const SellButton: React.FC<SellButtonProps> = ({
  securityPosition,
  sellOrderBlockedAvailabilityMessage,
  accountId,
  securityId,
}) => {
  if (!securityPosition) return null;

  if (sellOrderBlockedAvailabilityMessage) {
    return <ActionBlockedDialog direction="Sell" message={sellOrderBlockedAvailabilityMessage} />;
  }

  return (
    <SambaAutoLink
      to={urlBuilder.appAccountSellOrder.absolute({ accountId, securityId })}
      variant="solidSecondary"
      spacing="compact"
      css={{ flex: 1 }}
    >
      Sell
    </SambaAutoLink>
  );
};

export const ActionsCard: React.FC<Props> = ({ accountId, securityId }) => {
  const { data } = useGraphQLQuery(DirectTradingAccountBuySellAvailabilityDocument, {
    variables: { accountId, securityId },
    throwOnError: true,
  });

  if (!data) return <CardLoader />;
  if (!data.account || data.account.__typename !== 'DirectTradingAccount') {
    return null;
  }

  const { buyOrderBlockedAvailabilityMessage, sellOrderBlockedAvailabilityMessage, securityPosition } = data.account;

  return (
    <Card css={{ display: 'grid', gap: 'space.300', padding: 'space.300' }}>
      <Card.Content
        css={{
          gap: 'space.150',
          display: 'flex',
          flexDirection: 'row',
        }}
      >
        {buyOrderBlockedAvailabilityMessage ? (
          <ActionBlockedDialog direction="Buy" message={buyOrderBlockedAvailabilityMessage} />
        ) : (
          <SambaAutoLink
            to={urlBuilder.appAccountBuyOrder.absolute({ accountId, securityId })}
            variant="solidPrimary"
            spacing="compact"
            css={{ flex: 1 }}
          >
            Buy
          </SambaAutoLink>
        )}
        <SellButton
          securityPosition={securityPosition}
          sellOrderBlockedAvailabilityMessage={sellOrderBlockedAvailabilityMessage}
          accountId={accountId}
          securityId={securityId}
        />
      </Card.Content>
    </Card>
  );
};
